// #294 — a critical event in the dashboard's Recent Events. Redesign 2026-10-12 (CP 294 Section 1.3): a calm
// light-red card with a solid red bar on the left (index.css .crit-row), a small red "CRITICAL · Spray sound"
// label, the camera in bold, time, the title with the score, and a separate Acknowledge button. Open: the slow
// red running light around the edge (always on, not tied to the user's amber-light switch; off with Reduce
// motion), and a soft red glow for 2 s when it arrived while the page was open (`justArrived`). After
// acknowledging: grey, "Acknowledged by <name> · <time>", no light, no button. Opening the event and
// acknowledging it are two separate real buttons (a button inside a button is invalid HTML and breaks
// keyboard use).
import { forwardRef, type HTMLAttributes } from 'react'
import { useTranslation } from 'react-i18next'
import { Activity } from 'lucide-react'
import type { Event } from '@/types'
import { cn, formatCardDateTime } from '@/lib/utils'
import { criticalClueWords, criticalReasonCodes, criticalReasonText, isOpenCritical, type Translate } from '@/lib/criticalEvents'
import { useEventTitle } from '@/hooks/useEventTitle'
import type { RecentEventPreviewRowProps } from './RecentEventPreview'
import { UnreadDot } from './UnreadDot'
import { LevelBand, LevelIcon } from './LevelBand'
import { UnreadRowLight } from './UnreadRowLight'
import { useAlertLinked } from '@/store/alertLinkStore'

/** The red reason chip ("SPRAY SOUND") of the event popup; grey inside an acknowledged row. #408 — clues: "SUSPICIOUS
 *  PERSON", then one chip per clue word. */
export function CriticalReasonChips({ event, className }: { event: Event; className?: string }) {
  const { t } = useTranslation('common')
  return (
    <>
      {criticalReasonCodes(event).map((code) => (
        <span key={code} className={cn('crit-reason', className)}>{t(`critical.reasons.${code}`)}</span>
      ))}
      {criticalClueWords(event, t as unknown as Translate).map((word) => (
        <span key={word} className={cn('crit-reason', className)}>{word.toLocaleUpperCase()}</span>
      ))}
    </>
  )
}

interface Props extends HTMLAttributes<HTMLDivElement> {
  event: Event
  /** The hover / focus preview's handlers (RecentEventPreview); they go on the open button. */
  row: RecentEventPreviewRowProps
  onOpen: () => void
  onAcknowledge: () => void
  acknowledging?: boolean
  /** It arrived while the page was open (not on the first load): the 2 s arrival glow. */
  justArrived?: boolean
}

export const CriticalEventRow = forwardRef<HTMLDivElement, Props>(function CriticalEventRow(
  { event, row, onOpen, onAcknowledge, acknowledging, justArrived, className, ...rest }, ref,
) {
  const { t } = useTranslation('common')
  const title = useEventTitle()
  const live = isOpenCritical(event)
  const linked = useAlertLinked(event.id)
  const reason = criticalReasonText(event, t as unknown as Translate)
  const labelText = <><b>{t('critical.label')}</b>{reason ? <> · <span>{reason}</span></> : null}</>
  // final review I2 — the band's words, in the open button for screen readers (the band sits outside the button)
  const bandWords = reason ? `${t('critical.label')} · ${reason}` : t('critical.label')

  return (
    <div
      ref={ref}
      {...rest}
      data-critical-row={live ? 'open' : 'acknowledged'}
      data-alert-row={event.id}
      data-alert-linked={linked ? '' : undefined}
      className={cn(
        'crit-row overflow-hidden rounded-lg my-1',
        live ? 'crit-live' : 'crit-acked',
        live && justArrived && 'crit-arrive',
        className,
      )}
    >
      {/* the item 301 running light, in red: always on for an open critical row (index.css) */}
      {live && <UnreadRowLight variant="critical" />}
      {live && <LevelBand level="critical" ariaHidden>{labelText}</LevelBand>}
      <div className="flex w-full flex-wrap items-center gap-x-3 gap-y-2 py-2.5 pl-3.5 pr-2.5 sm:gap-x-4 sm:py-3">
        <button
          type="button"
          {...row}
          onClick={() => { row.onClick(); onOpen() }}
          className={cn(
            'flex min-w-[13rem] flex-1 items-center gap-3 sm:gap-4 rounded-md text-left',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary',
          )}
        >
          {live && <span className="sr-only">{bandWords}</span>}
          {event.thumbnail_url ? (
            <img
              src={event.thumbnail_url}
              alt=""
              loading="lazy"
              className="h-16 w-24 sm:h-20 sm:w-[120px] flex-shrink-0 rounded-md object-cover bg-muted"
            />
          ) : (
            <div className="flex h-16 w-24 sm:h-20 sm:w-[120px] flex-shrink-0 items-center justify-center rounded-md bg-red-500/10">
              <Activity className="h-5 w-5 text-red-700 dark:text-red-300" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            {!live && (
              <span className="crit-label">
                <LevelIcon level="critical" className="mr-1.5" />{labelText}
              </span>
            )}
            <div className="mt-0.5 flex flex-col sm:flex-row sm:items-baseline justify-between gap-0.5 sm:gap-3">
              <p className="flex min-w-0 items-baseline gap-2 text-[15px] leading-snug">
                {event.is_unread && <UnreadDot className="self-center" />}
                <strong className="truncate font-bold">{event.camera_name}</strong>
                {event.camera_group && <span className="crit-muted truncate text-[13px]">· {event.camera_group}</span>}
              </p>
              <span
                className="crit-muted text-xs sm:text-[13px] whitespace-nowrap tabular-nums flex-shrink-0"
                title={new Date(event.occurred_at).toLocaleString()}
              >
                {formatCardDateTime(event.occurred_at)}
              </span>
            </div>
            <p className="crit-sub truncate text-[13px] sm:text-sm leading-snug">{title(event)}</p>
            {!live && event.acknowledged_at && (
              <p className="mt-1 text-[13px] font-semibold leading-snug text-emerald-700 dark:text-emerald-400">
                {event.acknowledged_by
                  ? t('critical.acknowledgedBy', { name: event.acknowledged_by, time: formatCardDateTime(event.acknowledged_at) })
                  : t('critical.acknowledgedNoName', { time: formatCardDateTime(event.acknowledged_at) })}
              </p>
            )}
          </div>
        </button>
        {live && (
          <button type="button" className="crit-ack ml-auto" onClick={onAcknowledge} disabled={acknowledging}>
            {t('critical.acknowledge')}
          </button>
        )}
      </div>
    </div>
  )
})
